import { DialogTrigger, Input } from "@light-technology/components";
import { Check, Copy01 } from "@untitledui/icons";
import { type ReactNode, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { api } from "../../state/api";
import { type AppResponse } from "../../state/api.types";
import { BasicModal } from "../shared/BasicModal";
import { FormModal } from "../shared/FormModal";

type CreateKeyForm = {
  name: string;
};

type CreateKeyModalProps = {
  app: AppResponse;
  children: ReactNode;
};

export const CreateKeyModal = (props: CreateKeyModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const [createAPIKey, createAPIKeyResult] = api.useCreateAPIKeyMutation();
  const { handleSubmit, control, reset } = useForm<CreateKeyForm>({
    defaultValues: {
      name: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const onSubmit = handleSubmit(({ name }) => {
    createAPIKey({ appUUID: props.app.uuid, name });
  });

  if (createAPIKeyResult.isSuccess) {
    return (
      <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
        {props.children}
        <BasicModal
          textTitle="Here's your new app token"
          textDescription="Copy this app token and keep it somewhere safe. It is not possible to retrieve it again."
          textCancel="Close"
          textSubmit="Copy token"
          iconSubmit={<Copy01 />}
          iconSubmitOnClick={<Check />}
          isDisabled={false}
          isLoading={false}
          onCancel={() => {
            setIsOpen(false);
          }}
          onSubmit={() => {
            navigator.clipboard.writeText(createAPIKeyResult.data.token);
          }}
        >
          <div className="font-mono text-sm break-all">
            {createAPIKeyResult.data.token}
          </div>
        </BasicModal>
      </DialogTrigger>
    );
  }

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      {props.children}
      <FormModal
        textTitle="What do you want to name your app token?"
        textCancel="Cancel"
        textSubmit="Create token"
        isLoading={createAPIKeyResult.isLoading}
        onCancel={() => {
          setIsOpen(false);
        }}
        onSubmit={onSubmit}
        data-ph-capture-attribute-action="submit-create-api-token"
      >
        <Controller
          control={control}
          name="name"
          rules={{ required: "Name is required" }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              type="text"
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
          )}
        />
      </FormModal>
    </DialogTrigger>
  );
};
